import { Link } from 'react-router-dom';
import SEOHead, { seoConfigs } from '@/components/SEOHead';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { ChevronRight, Home } from 'lucide-react';

const SITE_URL = 'https://www.practice4sat.com';

const structuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  'name': 'Privacy Policy | Practice4SAT',
  'description': 'Learn how Practice4SAT protects your privacy and handles personal data. Our commitment to data security and user privacy.',
  'url': `${SITE_URL}/privacy-policy`,
  'inLanguage': 'en-US',
  'isPartOf': {
    '@type': 'WebSite',
    'name': 'Practice4SAT',
    'url': SITE_URL
  },
  'publisher': {
    '@type': 'Organization',
    'name': 'Practice4SAT',
    'url': SITE_URL
  },
  'dateModified': '2026-01-01',
  'breadcrumb': {
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': SITE_URL },
      { '@type': 'ListItem', 'position': 2, 'name': 'Privacy Policy', 'item': `${SITE_URL}/privacy-policy` }
    ]
  }
};

const sectionStyle = { marginBottom: '2rem' };
const headingStyle = { fontSize: '1.1rem', fontWeight: '600', color: '#0030b3', marginBottom: '0.75rem', fontFamily: "'Arial', sans-serif" };
const paragraphStyle = { color: '#374151', lineHeight: '1.5', marginBottom: '1rem', fontFamily: "'Arial', sans-serif" };

export default function PrivacyPolicy() {
  return (
    <div style={{ minHeight: '100vh', background: '#fff', display: 'flex', flexDirection: 'column' }}>
      <SEOHead {...seoConfigs.privacyPolicy} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />

      <Header variant="public" />

      <main style={{ flex: 1 }}>
        {/* Breadcrumb navigation */}
        <nav aria-label="Breadcrumb" data-testid="breadcrumb-nav" style={{
          padding: '1rem 2rem 0',
          maxWidth: '1400px',
          margin: '0 auto',
        }}>
          <ol style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            listStyle: 'none',
            padding: 0,
            margin: 0,
            fontSize: '0.9rem',
            fontFamily: "'Arial', sans-serif",
          }}>
            <li>
              <Link to="/" style={{ color: '#6b7280', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <Home size={14} aria-hidden="true" />
                <span>Home</span>
              </Link>
            </li>
            <li aria-hidden="true" style={{ color: '#9ca3af' }}>
              <ChevronRight size={14} />
            </li>
            <li>
              <span aria-current="page" style={{ color: '#0030b3', fontWeight: '600' }}>
                Privacy Policy
              </span>
            </li>
          </ol>
        </nav>

        <article style={{ padding: '2rem 2rem', maxWidth: '1400px', margin: '0 auto' }}>
          <h1 data-testid="privacy-title" style={{ fontSize: '1.5rem', fontWeight: '600', textAlign: 'center', marginTop: 0, marginBottom: '0.5rem', color: '#0030b3' }}>
            Privacy Policy
          </h1>
          <p style={{ textAlign: 'center', maxWidth: '720px', margin: '0 auto 2rem', color: '#666', fontFamily: "'Arial', sans-serif" }}>
            Last updated: January 2026
          </p>

          <section aria-labelledby="info-collection" style={sectionStyle}>
            <h2 id="info-collection" style={headingStyle}>Information We Collect</h2>
            <p style={paragraphStyle}>
              We collect information that you provide directly to us, including your name, email address, account credentials, and test performance data. We do not directly collect technical tracking data ourselves. However, certain technical information such as device type, browser type, and usage data may be collected automatically by trusted third-party analytics and infrastructure providers when you interact with our platform. This information is used to operate, maintain, personalize, and improve our services, and to ensure platform security and reliability.
            </p>
          </section>

          <section aria-labelledby="info-use" style={sectionStyle}>
            <h2 id="info-use" style={headingStyle}>How We Use Your Information</h2>
            <p style={paragraphStyle}>
              We use the information we collect to provide and improve our services, deliver personalized learning experiences, operate adaptive testing and analytics features, communicate with you regarding your account or support requests, send important service-related notices, and comply with legal obligations. We do not use your personal data for unrelated purposes without your consent.
            </p>
          </section>

          <section aria-labelledby="info-sharing" style={sectionStyle}>
            <h2 id="info-sharing" style={headingStyle}>Information Sharing</h2>
            <p style={paragraphStyle}>
              We do not sell your personal information. We only share personal information with trusted third-party service providers who perform services on our behalf, such as hosting, analytics, customer support, and payment processing. These providers are contractually required to protect your data and use it solely for providing services to Practice4SAT. We may also disclose information if required by law or to protect the rights, safety, and integrity of our users and platform.
            </p>
          </section>

          <section aria-labelledby="data-security" style={sectionStyle}>
            <h2 id="data-security" style={headingStyle}>Data Security</h2>
            <p style={paragraphStyle}>
              We take reasonable and appropriate administrative, technical, and physical safeguards to protect your personal information from unauthorized access, alteration, disclosure, or destruction. These measures include secure servers, encrypted connections, access controls, and routine security monitoring. However, no system can be guaranteed to be 100% secure.
            </p>
          </section>

          <section aria-labelledby="your-rights" style={sectionStyle}>
            <h2 id="your-rights" style={headingStyle}>Your Rights</h2>
            <p style={paragraphStyle}>
              You have the right to access, correct, update, or delete your personal information, subject to applicable legal requirements. Depending on your location, you may also have the right to restrict or object to certain processing activities and to request a copy of your data. To exercise these rights, please contact us at support@practice4sat.com. We will respond within the time required by applicable law.
            </p>
          </section>

          <section aria-labelledby="childrens-privacy" style={sectionStyle}>
            <h2 id="childrens-privacy" style={headingStyle}>Children's Privacy</h2>
            <p style={paragraphStyle}>
              We do not knowingly collect personal information from children under 13 without verifiable parental consent. If we become aware that we have collected such information without proper authorization, we will promptly delete it.
            </p>
          </section>

          <section aria-labelledby="cookies" style={sectionStyle}>
            <h2 id="cookies" style={headingStyle}>Cookies and Tracking Technologies</h2>
            <p style={paragraphStyle}>
              We use cookies and similar technologies to operate our platform, analyze usage, improve performance, and personalize your experience. Some cookies may be set by third-party service providers that support our analytics and infrastructure. You may control cookie settings through your browser preferences. Disabling cookies may affect certain features of the service.
            </p>
          </section>

          <section aria-labelledby="data-retention" style={sectionStyle}>
            <h2 id="data-retention" style={headingStyle}>Data Retention</h2>
            <p style={paragraphStyle}>
              We retain personal information only for as long as necessary to fulfill the purposes described in this policy, comply with legal obligations, resolve disputes, and enforce our agreements.
            </p>
          </section>

          <section aria-labelledby="international-rights" style={sectionStyle}>
            <h2 id="international-rights" style={headingStyle}>International and Regional Rights</h2>
            <p style={paragraphStyle}>
              If you are located in the European Economic Area, United Kingdom, or California, you may have additional rights under applicable data protection laws, including the right to know what personal data we collect and how it is used. We honor these rights in accordance with applicable regulations.
            </p>
          </section>

          <section aria-labelledby="policy-changes" style={sectionStyle}>
            <h2 id="policy-changes" style={headingStyle}>Changes to This Policy</h2>
            <p style={paragraphStyle}>
              We may update this Privacy Policy from time to time to reflect changes in our practices, technology, or legal requirements. Any updates will be posted on this page, and the "Last updated" date will be revised accordingly. Continued use of our services after changes take effect constitutes acceptance of the updated policy.
            </p>
          </section>

          <section aria-labelledby="contact-us" style={sectionStyle}>
            <h2 id="contact-us" style={headingStyle}>Contact Us</h2>
            <p style={paragraphStyle}>
              If you have any questions about this Privacy Policy or our data practices, please contact us at <strong style={{ color: '#91ccff' }}>support@practice4sat.com</strong>.
            </p>
          </section>

          {/* Internal links for SEO */}
          <aside style={{ marginTop: '1rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb' }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#374151', marginBottom: '0.75rem', fontFamily: "'Arial', sans-serif" }}>
              Explore Practice4SAT
            </h2>
            <nav aria-label="Related pages" style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
              <Link to="/quick-quiz" data-testid="link-diagnostic-quiz" style={{ color: '#0030b3', textDecoration: 'underline', fontSize: '0.95rem', fontFamily: "'Arial', sans-serif" }}>
                Free SAT Diagnostic Quiz
              </Link>
              <a href="/#pricing" data-testid="link-study-plan" style={{ color: '#0030b3', textDecoration: 'underline', fontSize: '0.95rem', fontFamily: "'Arial', sans-serif" }}>
                Get Your Personalized Study Plan
              </a>
            </nav>
          </aside>
        </article>
      </main>

      <Footer />

      <style>{`
        @media (min-width: 768px) {
          .desktop-nav {
            display: flex !important;
          }
          .mobile-menu-btn {
            display: none !important;
          }
        }
        @media (max-width: 767px) {
          .desktop-nav {
            display: none !important;
          }
        }
        .footer-link:hover {
          color: #91ccff !important;
        }
      `}</style>
    </div>
  );
}
